/**
 * Layer B feature guides: 1–3 steps per surface.
 * @typedef {{ id: string, title: string, body: string, previewKey?: string }} SurfaceGuideStep
 */

/** @type {Record<string, { title: string, steps: SurfaceGuideStep[] }>} */
const SURFACE_GUIDES = {
  'admin.clients': {
    title: 'Clients',
    steps: [
      {
        id: '1',
        title: 'Client companies',
        previewKey: 'clientList',
        body: 'Each row is a company you support. Use Add client to create a record before starting campaigns.',
      },
      {
        id: '2',
        title: 'What happens next',
        body: 'After you add a client, create a campaign under Campaigns and open it for casting and content work.',
      },
    ],
  },
  'admin.campaigns': {
    title: 'Campaigns',
    steps: [
      {
        id: '1',
        title: 'Campaign list',
        previewKey: 'campaignList',
        body: 'Create opens a quick form, then lands you on campaign detail with Casting in focus.',
      },
      {
        id: '2',
        title: 'Where work happens',
        previewKey: 'phaseStripAm',
        body: 'Inside a campaign you move through Casting, Creator pipeline, and Content review.',
      },
    ],
  },
  'campaign.casting': {
    title: 'Casting',
    steps: [
      {
        id: '1',
        title: 'Propose creators',
        previewKey: 'castingRoster',
        body: 'Add creators from your library, then send the list to your client for approval.',
      },
      {
        id: '2',
        title: 'Slots and invites',
        body: 'After approval, add deliverable slots with briefs. When every approved creator is invite-ready, send invites from Casting.',
      },
    ],
  },
  'campaign.pipeline': {
    title: 'Creator pipeline',
    steps: [
      {
        id: '1',
        title: 'Track progress',
        previewKey: 'pipelineColumns',
        body: 'See who accepted, who is working, and who is done. Use this after invites go out.',
      },
    ],
  },
  'campaign.content_review': {
    title: 'Content review',
    steps: [
      {
        id: '1',
        title: 'Review and release',
        previewKey: 'contentReview',
        body: 'Review submitted content, then Release to client when it is ready for their approval.',
      },
    ],
  },
  'client.approvals': {
    title: 'Approvals',
    steps: [
      {
        id: '1',
        title: 'Your queue',
        previewKey: 'clientQueues',
        body: 'Roster and content waiting on you appear here across all campaigns.',
      },
    ],
  },
  'client.campaigns': {
    title: 'My Campaigns',
    steps: [
      {
        id: '1',
        title: 'Browse campaigns',
        previewKey: 'campaignList',
        body: 'Open a campaign for casting work or content ready for your review.',
      },
    ],
  },
  'influencer.tasks': {
    title: 'My Tasks',
    steps: [
      {
        id: '1',
        title: 'Upload here only',
        previewKey: 'taskList',
        body: 'Open a task to read the brief, upload your file, and submit for review. Do not use a separate upload path on the campaign page.',
      },
    ],
  },
  'influencer.campaigns': {
    title: 'My Campaigns',
    steps: [
      {
        id: '1',
        title: 'Invites and context',
        previewKey: 'influencerQueues',
        body: 'Accept invites and read campaign details here. Upload content in My Tasks; after approval, submit post metrics on the campaign page.',
      },
    ],
  },
  'influencer.post_metrics': {
    title: 'Post metrics',
    steps: [
      {
        id: '1',
        title: 'When and where',
        previewKey: 'influencerQueues',
        body: 'After your content is approved and live, open the campaign and use the Post metrics section. Upload a screenshot of your insights—your account manager reviews before analytics update.',
      },
    ],
  },
  'influencer.submissions': {
    title: 'My Submissions',
    steps: [
      {
        id: '1',
        title: 'History and status',
        previewKey: 'contentReview',
        body: 'See what you submitted and what was approved. If revision is requested, the task will show up in My Tasks again.',
      },
    ],
  },
}

/**
 * @param {string} surfaceId
 * @returns {{ title: string, steps: SurfaceGuideStep[] } | null}
 */
export function getSurfaceGuideContent(surfaceId) {
  return SURFACE_GUIDES[surfaceId] ?? null
}
